import type { VersionView } from '@tabel/shared/schemas/rules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartColumn, Undo2 } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { errorText, useToast } from '../../components/ErrorToast';
import { Button, Skeleton, textareaCls } from '../../components/ui';
import { type RollbackResult, api, rulesKeys } from '../../lib/api';
import { ApiError, isTransient, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { DaysDialog } from './DaysDialog';
import { DialogFooter, GuardedModal, type ModalControl, useDirty } from './GuardedModal';
import { impactLine } from './ImpactLines';
import { shouldReport } from './LoadError';
import { goldenChangeText } from './golden';
import { LABEL, T, VERSION, say } from './texts';

/*
 * Окно «Вернуть правила версии N?» (004, US4, T089; contracts/ui.md, «Окна»; FR-047–FR-050; сценарии US4 2, 3, 6):
 * - какие правила вернутся: «Снова будут действовать правила версии N…»;
 * - влияние возврата (`POST /api/rules/versions/:id/rollback-preview`), «В каких днях»;
 * - все проверочные примеры объекта с результатом: возврат они не блокируют (FR-050);
 * - «Почему возвращаем» — обязательно; причина недоступности «Вернуть» — рядом с кнопкой.
 * Возврат недоступен, если на объекте черновик (`DRAFT_OPEN`) или правила версии система больше не умеет считать
 * (`IMPL_GONE`): окно пишет причину словами. Ошибка возврата — тост с кодом, введённое остаётся (FR-061). Часы окно
 * не считает: числа влияния и примеров — с сервера.
 */

/** Отказы влияния, при которых возврат невозможен: причина словами вместо «Повторить». */
const BLOCKED: Readonly<Record<string, string>> = {
  IMPL_GONE: VERSION.implGone,
  DRAFT_OPEN: VERSION.draftOpen,
};

function blockedText(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  return BLOCKED[e.code] ?? (e.code === 'VERSION_NOT_PAST' || e.code === 'NOT_FOUND' ? errorText(e) : null);
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} className="space-y-1.5">
      <h3 className="text-[13.5px] font-medium text-ink">{label}</h3>
      {children}
    </section>
  );
}

function RollbackBody({
  version,
  ctl,
  onDone,
}: {
  version: VersionView;
  ctl: ModalControl;
  onDone: (res: RollbackResult) => void;
}) {
  const ids = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [text, setText] = useState('');
  const [days, setDays] = useState(false);
  useDirty(ctl, text.trim() !== '');

  const preview = useQuery({
    queryKey: rulesKeys.rollbackPreview(role, version.id),
    queryFn: async () => {
      try {
        return await api.rollbackPreview(version.id);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'rollback preview', e);
        throw e;
      }
    },
    // Влияние — на момент открытия окна: данные табелей меняются, прежний расчёт не берётся.
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const impact = preview.data;
  const blocked = blockedText(preview.error);

  const rollback = useMutation({
    mutationFn: () => api.rollback(version.id, text.trim()),
    onSuccess: (res) => {
      showInfo(VERSION.rolledBack);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      onDone(res);
    },
    onError: (e) => {
      // Черновик появился или версия изменилась — история и правила перечитываются.
      if (e instanceof ApiError && ['DRAFT_OPEN', 'VERSION_NOT_PAST', 'IMPL_GONE'].includes(e.code))
        void qc.invalidateQueries({ queryKey: rulesKeys.all });
      showError(e, {
        screen: 'Rules',
        action: 'rollback',
        retry: isTransient(e) ? () => rollback.mutate() : undefined,
      });
    },
  });

  // Почему «Вернуть» недоступно — первая причина по порядку.
  const reason: string | null =
    blocked ??
    (preview.isFetching
      ? T.whyComputing
      : !impact
        ? T.whyImpact
        : text.trim() === ''
          ? VERSION.whyRollback
          : null);

  let impactBox: ReactNode;
  if (preview.isFetching) {
    impactBox = (
      <div aria-busy="true" aria-label={LABEL.computing} className="flex items-center gap-3">
        <span className="text-ink-2">{LABEL.computing}</span>
        <Skeleton className="h-3 min-w-24 flex-1" />
      </div>
    );
  } else if (blocked) {
    // Вернуть нельзя — влияния нет; почему — рядом с кнопкой «Вернуть».
    impactBox = null;
  } else if (preview.error || !impact) {
    const id = preview.error instanceof ApiError ? preview.error.requestId : null;
    const why = errorText(preview.error, '');
    impactBox = (
      <div role="alert" className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-bad">
          {why ? `${T.impactError} ${why}.` : T.impactError}
          {id && <span className="block text-[12px] text-ink-3">{say.errorCode(id)}</span>}
        </p>
        <Button onClick={() => void preview.refetch()}>{LABEL.retry}</Button>
      </div>
    );
  } else {
    impactBox = (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="min-w-0 flex-1 basis-[260px]">{impactLine(VERSION.impact, impact.summary)}</p>
        {impact.days.length > 0 && (
          <Button variant="ghost" onClick={() => setDays(true)}>
            {LABEL.whichDays}
          </Button>
        )}
        <DaysDialog open={days} days={impact.days} onClose={() => setDays(false)} />
      </div>
    );
  }

  const goldens = impact?.goldens ?? [];
  return (
    <div className="space-y-5 text-[13.5px]">
      {impactBox && (
        <div className="flex items-start gap-2.5 rounded-[10px] border border-dashed border-line-strong px-3 py-2.5">
          <ChartColumn size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
          <div className="min-w-0 flex-1">{impactBox}</div>
        </div>
      )}

      {goldens.length > 0 && (
        <Section label={LABEL.goldens}>
          <ul className="space-y-1">
            {goldens.map((g) => {
              const title = g.title ?? g.code;
              return (
                <li
                  key={g.goldenId}
                  className={`rounded-[8px] border px-3 py-1.5 ${g.passed ? 'border-line text-ink-2' : 'border-warn/40 bg-warn-soft text-ink'}`}
                >
                  {g.passed ? say.goldenSame(title) : goldenChangeText(title, g.before, g.after)}
                </li>
              );
            })}
          </ul>
          <p className="text-[12.5px] text-ink-2">{VERSION.rollbackGoldens}</p>
        </Section>
      )}

      <div className="space-y-1.5">
        <label htmlFor={`${ids}-reason`} className="block text-[13.5px] font-medium text-ink">
          {VERSION.rollbackReason}
        </label>
        <textarea
          id={`${ids}-reason`}
          data-autofocus
          rows={3}
          maxLength={500}
          value={text}
          placeholder={VERSION.rollbackPlaceholder}
          onChange={(e) => setText(e.target.value)}
          className={textareaCls}
        />
      </div>

      <DialogFooter reasonId={`${ids}-why`} reason={reason}>
        {/* «Закрыть», а не «Отмена»: рядом с «Вернуть правила» и «Не вводить» «Отмена» читается как «отменить версию» (ревью T103, С-2). */}
        <Button onClick={ctl.requestClose}>{LABEL.close}</Button>
        <Button
          variant="danger"
          icon={Undo2}
          disabled={reason !== null || rollback.isPending}
          aria-describedby={reason ? `${ids}-why` : undefined}
          onClick={() => rollback.mutate()}
        >
          {VERSION.rollbackDo}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * Окно возврата к версии `version` («действовала»); `null` — окно закрыто. После возврата страница перечитывает
 * правила и историю: в ней новая версия и «Система пересчитывает табели…».
 */
export function RollbackDialog({
  version,
  onClose,
  onDone,
}: {
  version: VersionView | null;
  onClose: () => void;
  onDone?: (res: RollbackResult) => void;
}) {
  return (
    <GuardedModal
      open={version !== null}
      onClose={onClose}
      title={version ? say.rollbackTitle(version.version) : ''}
      description={version ? say.rollbackText(version.version, version.validFrom) : undefined}
      width="w-[560px]"
    >
      {(ctl) =>
        version && (
          <RollbackBody
            version={version}
            ctl={ctl}
            onDone={(res) => {
              ctl.setDirty(false);
              onDone?.(res);
              onClose();
            }}
          />
        )
      }
    </GuardedModal>
  );
}
